import { XMarkIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { VideoSideBarV2 } from "@components/VideoSideBarV2/VideoSideBar";
import { VideoComments } from "@pages/VideoSessionsV2/components/Comments/VideoComments";
import { HeaderContent } from "@pages/VideoSessionsV2/components/HeaderContent";
import { VideoMoments } from "@pages/VideoSessionsV2/components/Moments/VideoMoments";
import { VideoSummary } from "@pages/VideoSessionsV2/components/Summary/VideoSummary";
import { VideoTranscript } from "@pages/VideoSessionsV2/components/Transcript/VideoTranscript";
import { displayDate } from "@pages/VideoSessionsV2/components/Utils/formatDate";
import { VideoContainer } from "@pages/VideoSessionsV2/components/VideoContainer";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface VideoPlayerReadyEventI {
  duration: number;
}
declare global {
  interface WindowEventMap {
    videoPlayerReady: CustomEvent<VideoPlayerReadyEventI>;
  }
}

export const VideoSessionDetailsV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const startTime = parseFloat(searchParams.get("startTime") || "0");
  const endTime = parseFloat(searchParams.get("endTime") || "0");
  const [videoDuration, setVideoDuration] = useState(0);

  const [videoSeeking, setVideoSeeking] = useState<boolean>(false);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);

  const {
    selectedMedia,
    setSelectedMedia,
    selectedTab,
    currentTime,
    playerRef,
    clearFilters,
    isVideoDetailsPage,
    activeMomentId,
    setActiveMomentId,
  } = useVideoSessionData();

  const videoUrl = selectedMedia?.signedUploadUrl || "";

  const handleDurationChange = (duration: number) => {
    setVideoDuration(duration);
  };

  useEffect(() => {
    return () => {
      setSelectedMedia(null); // cleanup runs on unmount
    };
  }, []);
  useEffect(() => {
    if (location.state) {
      if (location.state.commentId) {
        setActiveCommentId(location.state.commentId);
      }
      if (location.state.momentId) {
        setActiveMomentId(location.state.momentId);
      }
      if (playerRef.current && videoUrl && videoDuration > 0) {
        if (location.state?.timestamp) {
          playerRef.current?.seekTo(location.state.timestamp);
        }
        if (location.state?.startTime && location.state?.endTime) {
          playerRef.current?.playInRange(location.state?.startTime - 5, location.state?.endTime);
        }
      }
    }
  }, [location.state, playerRef, videoUrl, videoDuration]);

  if (!selectedMedia || !selectedMedia.videoSession) return null;

  const handleTimeClick = (time: number) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(time);
    }
  };

  const handleMarkerClick = (timestamp: number, commentId: string) => {
    handleTimeClick(timestamp);
    setActiveCommentId(commentId);
  };
  const handleMomentMarkerClick = (startTime: number, endTime: number, momentId: string) => {
    if (playerRef.current) {
      playerRef.current.playInRange(startTime - 5, endTime);
    }
    setActiveMomentId(momentId);
  };

  const learnerId = selectedMedia.videoSession.sessionlearnerSet?.edges[0]?.node?.sessionLearners?.id;
  const learnerName = selectedMedia.videoSession.sessionlearnerSet?.edges[0]?.node?.sessionLearners?.learnermetadata;
  const learnerFullName = learnerName ? `${learnerName.firstName} ${learnerName.lastName}` : "";
  const providerMeta = selectedMedia.videoSession.sessionproviderSet?.edges[0]?.node?.provider?.providermetadata;
  const caregiverMeta = selectedMedia.videoSession.sessioncaregiverSet?.edges[0]?.node?.caregiver?.caregivermetadata;

  const sessionDate = selectedMedia.videoSession.createdDate;
  const startDate = new Date(sessionDate);
  const formattedDate = displayDate(videoDuration, startDate);
  const sessionRecorder = providerMeta || caregiverMeta;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="border-b border-gray-200 shrink-0">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex lg:items-center items-start">
            <button
              onClick={() => {
                setSelectedMedia(null);
                clearFilters();
                navigate("/video-sessions-v2");
              }}
              className="mr-4 p-2 pt-1 lg:pt-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <HeaderContent
              learnerId={learnerId!}
              learnerFullName={learnerFullName}
              providerLastName={sessionRecorder?.lastName ?? ""}
              providerFirstName={sessionRecorder?.firstName ?? ""}
              date={formattedDate}
              sessionRating={selectedMedia.videoSession.sessionRating ?? undefined}
              userType={caregiverMeta ? "Caregiver" : "Provider"}
            />
          </div>
        </div>
      </div>

      <div
        className={clsx(
          "flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-x-0 border-t border-limestone-200 overflow-y-auto",
          isVideoDetailsPage && "max-lg:min-h-[calc(100vh-18rem)] lg:overflow-y-auto lg:h-[calc(100vh-10rem)]",
        )}
      >
        <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 bg-limestone-50 flex flex-col  lg:py-4 lg:px-2">
          <div className="w-full lg:rounded-2xl bg-transparent lg:pt-4 6 px-0 flex flex-col">
            <div className="w-full space-y-4 lg:space-y-4">
              <div className={`relative w-full `}>
                <div className="relative" data-video-player>
                  {videoUrl && (
                    <>
                      <VideoContainer
                        videoUrl={videoUrl}
                        thumbnailManifest={selectedMedia?.thumbnailManifest}
                        onDurationChange={handleDurationChange}
                        onSeeking={() => setVideoSeeking(true)}
                        onSeeked={() => setVideoSeeking(false)}
                        onMarkerClick={handleMarkerClick}
                        onTimelineClick={handleTimeClick}
                        onMomentMarkerClick={handleMomentMarkerClick}
                        enabledFocus={false}
                        externalPlayerRef={playerRef}
                        data-video-container
                        onPlayerReady={() => {
                          if (startTime && endTime) {
                            playerRef.current?.playInRange(startTime - 5, endTime);
                          }
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-1 bg-white lg:overflow-y-auto lg:px-0 px-4 border-l border-limestone-200">
          <VideoSideBarV2 currentTime={currentTime} />
          {selectedTab === "activity" ? (
            <VideoComments
              sessionNote={selectedMedia.videoSession.comment ?? undefined}
              sessionRecorder={`${sessionRecorder?.firstName ?? "N/A"} ${sessionRecorder?.lastName ?? ""}`}
              onClick={handleTimeClick}
              activeCommentId={activeCommentId ?? ""}
            />
          ) : selectedTab === "transcript" ? (
            <VideoTranscript currentTime={currentTime} onClick={(time) => playerRef.current?.seekTo(time)} />
          ) : selectedTab === "moments" ? (
            <VideoMoments
              onClick={handleMomentMarkerClick}
              videoSeeking={videoSeeking}
              activeMomentId={activeMomentId ?? ""}
            />
          ) : selectedTab === "summary" ? (
            <VideoSummary
              onTimeStampClick={handleTimeClick}
              onDurationClick={(startTime, endTime) => {
                playerRef.current?.playInRange(startTime, endTime);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
